import React, { useEffect, useState, useMemo } from "react";
import { Layout, Typography, Spin, Select, Checkbox, message, Tabs } from "antd";
import { useTable } from "react-table";
import Sidebar from "./Sidebar";
import { BASE_URL } from "./config";
import HeaderBar from "./HeaderBar";
import Footer from "./Footer";

const { Content, Sider } = Layout;
const { Option } = Select;
const { TabPane } = Tabs;

const MeetingsPage = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderTimes, setReminderTimes] = useState({});
  const [activeTab, setActiveTab] = useState("upcoming");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);

      fetch(`${BASE_URL}/api_meetings.php?company_id=${parsedUser.company_id}`)
        .then((res) => res.json())
        .then((res) => {
          if (res.status === "success") setData(res.data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, []);

  const handleTimeChange = (value, meetingId) => {
    setReminderTimes((prev) => ({ ...prev, [meetingId]: value }));
  };

  const handleSendReminder = (meeting) => {
    const reminderTime = reminderTimes[meeting.id];
    if (!reminderTime) {
      message.warning("Please select reminder time first!");
      return;
    }

    const meetingDateTime = new Date(`${meeting.meeting_date} ${meeting.meeting_time}`);
    const now = new Date();
    const diffInHours = (meetingDateTime - now) / (1000 * 60 * 60);

    if (diffInHours > reminderTime) {
      message.warning(`Meeting is too far away.`);
      return;
    }

    let allEmails = [meeting.email];

    if (meeting.guests) {
      const guestEmails = meeting.guests.split(/[\s,]+/).filter(Boolean);
      allEmails = [...allEmails, ...guestEmails];
    }

    fetch(`${BASE_URL}/sendReminder.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        emails: allEmails,
        reminder_time: reminderTime,
        meeting_date: meeting.meeting_date,
        meeting_time: meeting.meeting_time,
        notes: meeting.notes,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.status === "success") message.success("Reminder sent!");
        else message.error("Failed to send reminder");
      })
      .catch(() => message.error("Error sending reminder"));
  };

  // -------------------------------
  // FILTER UPCOMING & PAST MEETINGS
  // -------------------------------
  const now = new Date();

  const upcomingMeetings = data.filter((m) => {
    const dateTime = new Date(`${m.meeting_date} ${m.meeting_time}`);
    return dateTime >= now;
  });

  const pastMeetings = data.filter((m) => {
    const dateTime = new Date(`${m.meeting_date} ${m.meeting_time}`);
    return dateTime < now;
  });

  // Which data to show based on tab
  const filteredData = activeTab === "upcoming" ? upcomingMeetings : pastMeetings;

  const columns = useMemo(
    () => [
    {
  Header: "ID",
  accessor: (_, rowIndex) => rowIndex + 1
},

      {
        Header: "Name",
        accessor: (row) => `${row.first_name} ${row.last_name}`,
      },

      { Header: "Email", accessor: "email" },
      { Header: "Date", accessor: "meeting_date" },
      { Header: "Time", accessor: "meeting_time" },
      { Header: "Guests", accessor: "guests" },
      { Header: "Notes", accessor: "notes" },
      { Header: "Web Preferences", accessor: "web" },
     {
  Header: "Confirmation",
  Cell: ({ row }) => {
    const meeting = row.original;

    return (
      <Select
        defaultValue={meeting.confirmation_status || "pending"}
        style={{ width: 120 }}
        onChange={(value) => handleConfirmationChange(value, meeting)}
      >
        <Option value="accepted">Accepted</Option>
        <Option value="rescheduled">Rescheduled</Option>
        
      </Select>
    );
  },
},


      {
        Header: "Reminder",
        Cell: ({ row }) => {
          const meeting = row.original;
          const meetingDateTime = new Date(`${meeting.meeting_date} ${meeting.meeting_time}`);
          const now = new Date();

          if (meetingDateTime < now) {
            return <span style={{ color: "red", fontSize: 12 }}>Meeting Passed</span>;
          }

          return (
            <div className="reminder-box">
              <Select
                placeholder="Select"
                size="small"
                className="reminder-select"
                onChange={(value) => handleTimeChange(value, meeting.id)}
              >
                <Option value="8">8 hrs</Option>
                <Option value="10">10 hrs</Option>
                <Option value="24">24 hrs</Option>
              </Select>

              <Checkbox onChange={() => handleSendReminder(meeting)} />
            </div>
          );
        },
      },
    ],
    [reminderTimes]
  );
const handleConfirmationChange = (status, meeting) => {
  const numericStatus = status === "accepted" ? 1 : 2; // match your PHP code
  let allEmails = [meeting.email];
  if (meeting.guests) {
    allEmails = [...allEmails, ...meeting.guests.split(/[\s,]+/).filter(Boolean)];
  }

  const payload = {
    meeting_id: meeting.id,
    status: numericStatus,
    emails: allEmails,
  };

  fetch(`${BASE_URL}/send_confirmation.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
    .then((res) => {
      // First check if response is JSON
      return res.text().then((text) => {
        try {
          const data = JSON.parse(text);
          console.log("Response JSON:", data);
        } catch (err) {
          console.error("Invalid JSON from server. Raw response:", text);
        }
      });
    })
    .catch((err) => console.error("Fetch error:", err));
};






  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: filteredData });

  return (
    <Layout style={{ minHeight: "100vh", fontFamily: "Poppins, sans-serif" }}>
      {/* Sidebar */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="md"
        collapsedWidth="70"
        style={{ background: "#fff" }}
      >
        <Sidebar collapsed={collapsed} user={user} />
      </Sider>

      <Layout>
        <HeaderBar title="Meetings" />

        <Content style={{ margin: "12px" }}>
          {loading ? (
            <div className="loading-container">
              <Spin size="large" tip="Loading meetings..." />
            </div>
          ) : (
            <>

              {/* -------------------------- */}
              {/*        TABS SECTION         */}
              {/* -------------------------- */}
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                style={{ marginBottom: 20 }}
              >
                <TabPane tab="Upcoming Meetings" key="upcoming" />
                <TabPane tab="Past Meetings" key="past" />
              </Tabs>

              {/* -------------------------- */}
              {/*       TABLE SECTION        */}
              {/* -------------------------- */}
              <div className="table-wrapper">
                <table {...getTableProps()} className="responsive-table">
                  <thead>
                    {headerGroups.map((headerGroup, idx) => (
                      <tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                        {headerGroup.headers.map((column, colIdx) => (
                          <th {...column.getHeaderProps()} key={colIdx}>
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody {...getTableBodyProps()}>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} className="no-data">
                          No meetings found
                        </td>
                      </tr>
                    ) : (
                      rows.map((row, rowIdx) => {
                        prepareRow(row);
                        return (
                          <tr key={rowIdx}>
                            {row.cells.map((cell, cellIdx) => (
                              <td key={cellIdx}>{cell.render("Cell")}</td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Content>

        <Footer user={user} />
      </Layout>

      {/* Styling */}
      <style>
        {`
          .table-wrapper {
            width: 100%;
            overflow-x: auto;
            border-radius: 10px;
            border: 1px solid #ddd;
            background: #fff;
          }

          .responsive-table {
            width: 100%;
            min-width: 900px;
            border-collapse: collapse;
            font-size: 13px;
          }

          thead {
            background: #343a40;
            color: white;
          }

          th, td {
            padding: 10px;
            border: 1px solid #ddd;
            text-align: center;
            word-break: break-word;
          }

          .no-data {
            padding: 10px;
            text-align: center;
          }

          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 60vh;
          }
        `}
      </style>
    </Layout>
  );
};

export default MeetingsPage;
